import React, { useMemo } from 'react';
import { Box, Flex, Image, Menu, MenuButton, MenuItem, MenuList, Spinner, Text } from '@chakra-ui/react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ChevronDownIcon } from '@chakra-ui/icons';
import { useBlockchainProvider } from '../../providers/Blockchain/blockchainProvider';
import { t } from 'i18next';
import { useWalletAccountProvider } from '../../providers/WalletAccount/walletAccountProvider';
import { trimAddress } from '../../utils/parser';
import { Color } from '../../theme/color';
import { getTransparent } from '../../utils/themer';
import { WalletWhiteIcon } from '../../common/components/icons/wallet_white';

enum AccountMiniType {
	DEFAULT,
	MOBILE,
}

export const AccountMini = ({ isMobile = false }: { isMobile?: boolean }) => {
	const { account } = useBlockchainProvider();
	const { data, isLoading } = useWalletAccountProvider();

	const navigate = useNavigate();
	const { pathname } = useLocation();

	const nav = [
		{
			name: t('profileMenu.account').toString(),
			url: `/user/${account.toLowerCase()}`,
			auth: 'all',
		},
	];

	const menuNav = nav.map(({ url, name }, index) => (
		<MenuItem
			key={`acc_${name}_${url}`}
			borderTopRadius={index === 0 ? '5px' : 0}
			borderBottomRadius={index + 1 === nav.length ? '5px' : 0}
			_focus={{ bg: pathname === url ? Color.ACTION : Color.BACKGROUND }}
			_hover={{ bg: Color.ACTION }}
			background={pathname === url ? Color.ACTION : Color.BACKGROUND}
			onClick={() => navigate(url)}
		>
			{name}
		</MenuItem>
	));

	const memoAvatar = useMemo(
		() =>
			isLoading ? (
				<Spinner color={Color.ACTION} />
			) : (
				<Image
					maxW="34px"
					maxH="34px"
					borderRadius="50%"
					src={data.avatar !== '' ? data.avatar : '/icons/default_avatar.png'}
				/>
			),
		[data.avatar, isLoading]
	);

	const typeOptions = {
		[AccountMiniType.DEFAULT]: () => (
			<Flex
				alignItems="center"
				gridGap="13px"
			>
				<Box textAlign="end">
					<Text
						fontSize="12px"
						color="text"
						fontWeight="600"
					>
						{data.nickname}
					</Text>
					<Text
						fontSize="12px"
						color={`${getTransparent(Color.TEXT, 50)}`}
						fontWeight="400"
					>
						{trimAddress(account)}
					</Text>
				</Box>
				<Flex
					cursor="pointer"
					position="relative"
					color="black"
					fontWeight="600"
					fontSize="12px"
					alignItems="center"
					justifyContent="center"
				>
					<Flex
						alignItems="center"
						justifyContent="center"
						position="relative"
						borderRadius="50%"
						border="1.6px solid rgba(255, 255, 255, 0.3)"
						mr="8px"
						h="44px"
						w="44px"
					>
						<Box
							w="45px"
							h="22px"
							pos="absolute"
							backgroundColor="transparent"
							borderTopLeftRadius="22px"
							borderTopRightRadius="22px"
							borderWidth="2px 2px 0px"
							borderTopStyle="solid"
							borderRightStyle="solid"
							borderLeftStyle="solid"
							borderTopColor="action"
							borderRightColor="action"
							borderLeftColor="action"
							borderBottomStyle="initial"
							borderBottomColor="initial"
							top="11.5px"
							right="0"
							bottom="0"
							left="-20px"
							margin="auto"
							transform="rotate(-120deg)"
						/>
						{memoAvatar}
					</Flex>
					<Flex
						h="15px"
						w="15px"
						bottom="-1px"
						justifyContent="center"
						alignItems="center"
						background="white"
						right="5px"
						borderRadius="50%"
						position="absolute"
					>
						<ChevronDownIcon color="action" />
					</Flex>
				</Flex>
			</Flex>
		),
		[AccountMiniType.MOBILE]: () => (
			<Flex
				alignItems="center"
				cursor="pointer"
			>
				{!isMobile && (
					<Text
						whiteSpace="nowrap"
						fontSize="16px"
						fontWeight="600"
						mr="6px"
					>
						{t('default.connect').toString()}
					</Text>
				)}
				<Box maxH="20px">
					<WalletWhiteIcon />
				</Box>
			</Flex>
		),
	};

	const connectType = useMemo(() => {
		if (isMobile) {
			return AccountMiniType.MOBILE;
		}
		return AccountMiniType.DEFAULT;
	}, [isMobile]);

	return (
		<Menu placement="bottom-end">
			<MenuButton>{typeOptions[connectType]()}</MenuButton>
			<MenuList
				maxW="100px"
				p="0"
				border={`1px solid ${Color.ACTION}`}
			>
				{menuNav}
			</MenuList>
		</Menu>
	);
};
